import React, { useMemo } from 'react';

import {
  Breakpoints,
  CTA,
  Divider,
  TextAppearance,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
} from '@vw-marketing/us-components';

import { AppConstants } from '../../../utils/app-constants';
import {
  MaintenanceLineItem,
  MaintenanceSchedulePackagesData,
  TimeBasedServices,
} from '../../../typings/general';
import { useMaintenanceContext } from '../../../hooks/use-maintenance-context';
import { useViewport, ViewportType } from '../../../hooks/use-viewport';
import {
  StyledContainer,
  StyledMaintenanceServicesHeadline,
  StyledServicesHeadline,
  StyledServicesItem,
  StyledServicesItemContainer,
  StyledServicesList,
  StyledTwoColumnServicesList,
} from './styled';
import { StyledViewAllCTA } from '../Content/styled';

interface ServicesLayerProps {
  packages?: MaintenanceSchedulePackagesData;
  additionalServices: Array<MaintenanceLineItem | TimeBasedServices>;
  showAllServices: boolean;
  openContentLayer?: boolean;
  onViewAllServices?: (value: boolean) => void;
  handleViewAllServicesTracking?: () => void;
  shouldShowViewAllButton?: boolean;
  showTitle?: boolean;
}

interface SectionLayoutConfig {
  sectionKey: string;
  isTwoColumn: boolean;
}

const ButtonText = ({ children }) => (
  <TextCustom
    appearance={TextAppearance.label200}
    tag={TextTag.p}
    style={{
      [Breakpoints.default]: {
        fontSize: TextSize.TextSize16,
        fontWeight: TextWeight.regular,
        lineHeight: TextLineHeight.TextLineHeight20,
      },
    }}
  >
    {children}
  </TextCustom>
);

export const ServicesLayer = ({
  packages,
  additionalServices,
  showAllServices,
  openContentLayer = false,
  onViewAllServices,
  handleViewAllServicesTracking,
  shouldShowViewAllButton,
  showTitle = true,
}: ServicesLayerProps) => {
  const { translationHandler } = useMaintenanceContext();
  const viewport = useViewport(Breakpoints.b560, Breakpoints.b1280);

  const maintenanceItemsSize = useMemo(
    () =>
      viewport === ViewportType.DESKTOP
        ? AppConstants.DESKTOP_MAINTEANCE_ITEMS_SIZE
        : AppConstants.DEFAULT_MAINTENANCE_ITEMS_SIZE,
    [viewport],
  );

  const sectionLayoutConfigs = useMemo((): SectionLayoutConfig[] => {
    if (!packages?.formattedNotes || viewport !== ViewportType.DESKTOP) {
      return [];
    }

    return Object.entries(packages.formattedNotes).map(
      ([description, formattedNotes]) => ({
        sectionKey: description,
        isTwoColumn: formattedNotes.length >= 10,
      }),
    );
  }, [packages?.formattedNotes, viewport, showAllServices]);

  const getSectionLayoutConfig = (
    sectionKey: string,
  ): SectionLayoutConfig | undefined => {
    return sectionLayoutConfigs.find(
      (config) => config.sectionKey === sectionKey,
    );
  };

  const shouldUseTwoColumns = (sectionKey: string): boolean => {
    const config = getSectionLayoutConfig(sectionKey);
    return config?.isTwoColumn ?? false;
  };

  const renderServiceItem = (
    items: any,
    itemIndex: number,
    sectionKey: string,
  ) => {
    let actionText = items.action;
    let partsText = items.parts;

    if (items.action && items.action.startsWith('Tires ') && !items.parts) {
      const parts = items.action.split(' ');
      partsText = parts[0];
      actionText = parts.slice(1).join(' ');
    }

    return (
      <StyledServicesItemContainer
        key={`${sectionKey}-${itemIndex}-${items.fullText}`}
      >
        <StyledServicesItem>
          <TextCustom
            appearance={TextAppearance.copy100}
            tag={TextTag.p}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize14,
                fontWeight: TextWeight.regular,
                lineHeight: TextLineHeight.TextLineHeight20,
              },
              [Breakpoints.b1920]: {
                fontSize: TextSize.TextSize16,
                fontWeight: TextWeight.regular,
                lineHeight: TextLineHeight.TextLineHeight24,
              },
            }}
          >
            {actionText}
          </TextCustom>
          <TextCustom
            appearance={TextAppearance.copy100}
            tag={TextTag.p}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize14,
                fontWeight: TextWeight.regular,
                lineHeight: TextLineHeight.TextLineHeight20,
              },
              [Breakpoints.b1920]: {
                fontSize: TextSize.TextSize16,
                fontWeight: TextWeight.regular,
                lineHeight: TextLineHeight.TextLineHeight24,
              },
            }}
          >
            {partsText}
          </TextCustom>
        </StyledServicesItem>
        <Divider />
      </StyledServicesItemContainer>
    );
  };

  const renderAdditionalServiceItem = (
    service: TimeBasedServices | MaintenanceLineItem,
    serviceIndex: number,
  ) => (
    <StyledServicesItemContainer
      key={`${service.short_description}-${serviceIndex}`}
    >
      <StyledServicesItem isAdditionalService={true}>
        <TextCustom
          appearance={TextAppearance.copy100}
          tag={TextTag.p}
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize14,
              fontWeight: TextWeight.regular,
              lineHeight: TextLineHeight.TextLineHeight20,
            },
            [Breakpoints.b1920]: {
              fontSize: TextSize.TextSize16,
              fontWeight: TextWeight.regular,
              lineHeight: TextLineHeight.TextLineHeight24,
            },
          }}
        >
          {service.description}
        </TextCustom>
        <TextCustom
          appearance={TextAppearance.copy100}
          tag={TextTag.p}
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize14,
              fontWeight: TextWeight.regular,
              lineHeight: TextLineHeight.TextLineHeight20,
            },
            [Breakpoints.b1920]: {
              fontSize: TextSize.TextSize16,
              fontWeight: TextWeight.regular,
              lineHeight: TextLineHeight.TextLineHeight24,
            },
          }}
        >
          {service.notes[0]}
        </TextCustom>
      </StyledServicesItem>
      <Divider />
    </StyledServicesItemContainer>
  );

  return (
    <StyledContainer
      data-testid="information-layer"
      openContentLayer={openContentLayer}
    >
      {packages?.formattedNotes &&
        Object.entries(packages.formattedNotes).map(
          ([description, formattedNotes]) => {
            const displayedNotes = showAllServices
              ? formattedNotes
              : formattedNotes.slice(0, maintenanceItemsSize);

            const currentSectionUseTwoColumns =
              shouldUseTwoColumns(description);

            const ItemContainerWrapper = currentSectionUseTwoColumns
              ? StyledTwoColumnServicesList
              : React.Fragment;

            return (
              <React.Fragment key={description}>
                <StyledMaintenanceServicesHeadline>
                  <TextCustom
                    appearance={TextAppearance.copy200}
                    tag={TextTag.p}
                    style={{
                      [Breakpoints.default]: {
                        fontSize: TextSize.TextSize20,
                        fontWeight: TextWeight.bold,
                        lineHeight: TextLineHeight.TextLineHeight24,
                      },
                      [Breakpoints.b1920]: {
                        fontSize: TextSize.TextSize32,
                        fontWeight: TextWeight.bold,
                        lineHeight: TextLineHeight.TextLineHeight36,
                      },
                    }}
                  >
                    {`${description} Service`}
                  </TextCustom>
                  <TextCustom
                    appearance={TextAppearance.copy100}
                    tag={TextTag.p}
                    style={{
                      [Breakpoints.default]: {
                        fontSize: TextSize.TextSize14,
                        fontWeight: TextWeight.regular,
                        lineHeight: TextLineHeight.TextLineHeight24,
                      },
                      [Breakpoints.b1920]: {
                        fontSize: TextSize.TextSize20,
                        fontWeight: TextWeight.regular,
                        lineHeight: TextLineHeight.TextLineHeight32,
                      },
                    }}
                  >
                    {translationHandler('maintenance.services.description')}
                  </TextCustom>
                </StyledMaintenanceServicesHeadline>

                <StyledServicesList>
                  <ItemContainerWrapper>
                    {displayedNotes.map((items, index) =>
                      renderServiceItem(items, index, description),
                    )}
                  </ItemContainerWrapper>

                  {shouldShowViewAllButton &&
                    onViewAllServices &&
                    handleViewAllServicesTracking && (
                      <>
                        <StyledViewAllCTA>
                          <CTA
                            tag="button"
                            emphasis="tertiary"
                            onClick={() => {
                              onViewAllServices(true);
                              handleViewAllServicesTracking();
                            }}
                          >
                            <ButtonText>
                              {translationHandler('maintenance.view.all')}
                            </ButtonText>
                          </CTA>
                        </StyledViewAllCTA>
                        <Divider />
                      </>
                    )}
                </StyledServicesList>
              </React.Fragment>
            );
          },
        )}

      {additionalServices.length > 0 && (
        <>
          {showTitle && (
            <StyledServicesHeadline>
              <TextCustom
                appearance={TextAppearance.copy200}
                tag={TextTag.p}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize20,
                    fontWeight: TextWeight.bold,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                  [Breakpoints.b1920]: {
                    fontSize: TextSize.TextSize32,
                    fontWeight: TextWeight.bold,
                    lineHeight: TextLineHeight.TextLineHeight36,
                  },
                }}
              >
                {translationHandler('maintenance.additional.services')}
              </TextCustom>
            </StyledServicesHeadline>
          )}
          <StyledServicesList>
            {additionalServices.map((service, index) =>
              renderAdditionalServiceItem(service, index),
            )}
          </StyledServicesList>
        </>
      )}
    </StyledContainer>
  );
};
